import { FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useMaintenancesApi } from "../../http/maintenances/api";
import { MaintenanceNoCarDependency } from "../../http/maintenances/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { convertKilometersToMiles, formatCurrency, formatDateNumeric, formatNumber } from "../../types/utils";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import CustomLink from "../ui/CustomLink";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction } from "../ui/ListItemMenu";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CreateMaintenanceDialog from "./CreateMaintenanceDialog";
import DeleteMaintenanceDialog from "./DeleteMaintenanceDialog";
import EditMaintenanceDialog from "./EditMaintenanceDialog";
import MaintenanceTypes from "./types/MaintenanceTypes";

const MAINTENANCE_FIELDS = [
  "id",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "date",
  "type.id",
  "type.name",
  "distance",
  "cost",
] as const;
type MaintenanceField = (typeof MAINTENANCE_FIELDS)[number];
type MaintenanceSearch = NestedPick<MaintenanceNoCarDependency, MaintenanceField>;

type MaintenanceProps = {
  onUpdate?: () => void;
};

const Maintenances: FC<MaintenanceProps> = ({ onUpdate }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const maintenancesApi = useMaintenancesApi();
  const filters = useListFilters({ isSearchable: true, prefix: "ma" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const distanceInMiles = !!userSettings.me.distanceInMiles;

  const [showNewItem, setShowNewItem] = useState(false);
  const [showMaintenanceTypes, setShowMaintenanceTypes] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MaintenanceSearch>();
  const [itemToEdit, setItemToEdit] = useState<MaintenanceSearch>();
  const { id: carId } = useParams<{ id: string }>();

  const query: PaginationQueryWithFields<MaintenanceSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: MAINTENANCE_FIELDS,
  };

  const result = maintenancesApi.useAdminMaintenance(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: maintenancesApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: MaintenanceSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.maintenances.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    if (userSettings.permissions.maintenances.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  const content = (
    <>
      <Table<MaintenanceSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: userSettings.permissions.maintenances.insert && (
            <Button buttonProps={{ onClick: () => setShowNewItem(true) }} glyph={SvgAdd} />
          ),
          secondary: userSettings.permissions.maintenanceTypes.read && (
            <Button
              buttonProps={{
                onClick: () => setShowMaintenanceTypes(true),
              }}
            >
              {i18n.t.common.manageMaintenanceTypes}
            </Button>
          ),
        }}
        head={
          <>
            {!carId && (
              <>
                <Table.Th
                  title={i18n.t.plateNumbers.singular}
                  fieldName="maintenance.car.plateNumber"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
                <Table.Th
                  title={i18n.t.common.carVin}
                  fieldName="maintenance.car.vin"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
              </>
            )}
            <Table.Th title={i18n.t.common.date} fieldName="maintenance.date" order={orderOptions} />
            <Table.Th
              title={i18n.t.maintenanceTypes.singular}
              fieldName="maintenance.type"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th title={i18n.t.common.distance} fieldName="maintenance.distance" order={orderOptions} />
            <Table.Th title={i18n.t.common.cost} fieldName="maintenance.cost" order={orderOptions} />
            <th></th>
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          setItemToEdit(item);
        }}
        renderRow={(item) => (
          <>
            {!carId && (
              <>
                <td>
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>
                      {item.car.currentPlateNumber || item.car.lastPlateNumber}
                    </CustomLink>
                  ) : (
                    <>{item.car.currentPlateNumber || item.car.lastPlateNumber}</>
                  )}
                </td>
                <td>
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.vehicleIdentificationNumber}</CustomLink>
                  ) : (
                    item.car.vehicleIdentificationNumber
                  )}
                </td>
              </>
            )}
            <td>{formatDateNumeric(item.date, i18n.language)}</td>
            <td>{item.type.name}</td>
            <Table.Td noWrap>
              {item.distance == undefined
                ? ""
                : formatNumber(
                    distanceInMiles ? convertKilometersToMiles(item.distance) : item.distance,
                    i18n.language,
                    {
                      showThousendSeparator: true,
                      fractionalDigits: 0,
                    },
                  ) + ` ${distanceInMiles ? i18n.t.common.miles : "km"}`}
            </Table.Td>
            <Table.Td noWrap>{item.cost != undefined ? formatCurrency(item.cost, i18n.language) : ""}</Table.Td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!itemToDelete && (
        <DeleteMaintenanceDialog
          maintenanceId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={(success) => {
            setItemToDelete(undefined);
            success && onUpdate?.();
          }}
        />
      )}

      {!!itemToEdit && (
        <EditMaintenanceDialog
          maintenanceId={itemToEdit.id}
          onClose={(success) => {
            setItemToEdit(undefined);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}

      {!!showNewItem && (
        <CreateMaintenanceDialog
          onClose={(success) => {
            setShowNewItem(false);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}

      {!!showMaintenanceTypes && <MaintenanceTypes onClose={() => setShowMaintenanceTypes(false)} />}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.maintenance.plural} />
      <Panel>{content}</Panel>
    </>
  );
};

export default Maintenances;
